// B1.4: move-only from client.ts.
import { ApiClientCore, ApiError, buildQueryString } from './http/core';

export class ChecklistsApi extends ApiClientCore {
  async addChecklistTemplateItem(templateId: number, data: any): Promise<any> {
    return this.post(`/api/v1/checklists/templates/${templateId}/items`, data);
  }
  async assignChecklistFlex(data: {
    template_id: number;
    location_id?: number;
    assignment_type: 'shift' | 'date' | 'employee' | 'batch';
    shift_id?: number;
    target_date?: string;
    employee_id?: number;
    allow_without_shift?: boolean;
    force_duplicate?: boolean;
    assignment_purpose?: 'training' | 'onboarding' | 'audit' | 'correction' | 'other';
    assignment_note?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<any> {
    return this.post('/api/v1/checklists/assign-flex', data);
  }
  async bulkActionChecklists(data: {
    ids: number[];
    action: 'cancel' | 'delete' | 'change_status';
    reason?: string;
    new_status?: string;
  }): Promise<{
    success_count: number;
    failed_count: number;
    failed_ids: number[];
    errors: { id: number; error: string }[];
  }> {
    return this.post('/api/v1/checklists/bulk-action', data);
  }
  // ─── Checklists Analytics ────────────────────────────────────────────────────
  async cancelChecklist(id: number, reason?: string): Promise<any> {
    return this.post(`/api/v1/checklists/${id}/cancel`, { reason: reason || null });
  }
  async createChecklistBlock(templateId: number, data: any): Promise<any> {
    return this.post(`/api/v1/checklists/templates/${templateId}/blocks`, data);
  }
  async createChecklistTemplate(data: any): Promise<any> {
    return this.post('/api/v1/checklists/templates', data);
  }
  async createContractorTemplate(data: {
    name: string;
    description?: string;
    contractor_role_label: string;
    location_ids: number[];
    qr_activation_enabled?: boolean;
    items: Array<{ title: string; item_type?: string; is_required?: boolean; order_index?: number }>;
  }): Promise<any> {
    return this.post('/api/v1/checklists/contractors/templates', data);
  }
  async deleteChecklistBlock(templateId: number, blockId: number): Promise<any> {
    return this.delete(`/api/v1/checklists/templates/${templateId}/blocks/${blockId}`);
  }
  async deleteChecklistTemplate(id: number): Promise<any> {
    return this.delete(`/api/v1/checklists/templates/${id}`);
  }
  async deleteChecklistTemplateItem(templateId: number, itemId: number): Promise<any> {
    return this.delete(`/api/v1/checklists/templates/${templateId}/items/${itemId}`);
  }
  async deleteContractorTemplate(id: number): Promise<void> {
    return this.delete(`/api/v1/checklists/contractors/templates/${id}`);
  }
  async downloadChecklistPdfBrandingPreview(): Promise<void> {
    const blob = await this.getBlob('/api/v1/checklists/pdf-branding/preview.pdf');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'checklist-preview.pdf';
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async downloadChecklistReportPdf(
    checklistId: number,
  ): Promise<{ blob: Blob; filename: string }> {
    const { parseContentDispositionFilename } = await import('@/lib/parse-content-disposition');
    const url = `${this.baseURL}/api/v1/checklists/${checklistId}/report.pdf`;
    const response = await this.authedFetch(url, { method: 'GET' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        (errorData as any).detail || (errorData as any).error || response.statusText,
        errorData
      );
    }
    const filename =
      parseContentDispositionFilename(response.headers.get('Content-Disposition')) ||
      `checklist-${checklistId}.pdf`;
    return { blob: await response.blob(), filename };
  }
  // ─── Checklists ────────────────────────────────────────────────────────────
  async downloadChecklistTemplateQrPrintPdf(templateId: number): Promise<void> {
    const blob = await this.getBlob(`/api/v1/checklists/templates/${templateId}/qr/print.pdf`);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `qr-template-${templateId}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  // ─── Contractor checklists (guest QR) ─────────────────────────────────────
  async downloadContractorTemplateQrPrintPdf(templateId: number): Promise<void> {
    const blob = await this.getBlob(
      `/api/v1/checklists/contractors/templates/${templateId}/qr/print.pdf`
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `qr-contractor-${templateId}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  async duplicateChecklistTemplate(templateId: number, newName: string): Promise<any> {
    return this.post(`/api/v1/checklists/templates/${templateId}/duplicate${buildQueryString({ new_name: newName })}`);
  }
  async duplicateContractorTemplate(
    templateId: number,
    newName: string
  ): Promise<{ new_template_id: number }> {
    return this.post(
      `/api/v1/checklists/contractors/templates/${templateId}/duplicate?new_name=${encodeURIComponent(newName)}`
    );
  }
  async getChecklist(id: number): Promise<any> {
    return this.get(`/api/v1/checklists/${id}`);
  }
  async getChecklistBlocks(templateId: number): Promise<any> {
    return this.get(`/api/v1/checklists/templates/${templateId}/blocks`);
  }
  async getChecklistCompletionEvents(id: number): Promise<
    Array<{
      id: number;
      action: string;
      created_at: string;
      user_name: string | null;
      item_title: string | null;
    }>
  > {
    return this.get(`/api/v1/checklists/${id}/completion-events`);
  }
  async getChecklistDelivery(checklistId: number): Promise<any> {
    return this.get(`/api/v1/checklists/${checklistId}/delivery`);
  }
  async getChecklistItemIncident(checklistItemId: number): Promise<{ incident: any | null }> {
    return this.get(`/api/v1/checklists/items/${checklistItemId}/incident`);
  }
  // ─── Consumables ────────────────────────────────────────────────────────────
  async getChecklistPdfBranding(): Promise<any> {
    return this.get('/api/v1/checklists/pdf-branding');
  }
  async getChecklistProblemItemsReport(params: {
    location_id?: number;
    date_from?: string;
    date_to?: string;
    limit?: number;
  } = {}): Promise<any> {
    return this.get(`/api/v1/checklists/report/problem-items${buildQueryString(params)}`);
  }
  async getChecklistRelatedIncidents(checklistId: number): Promise<any[]> {
    return this.get(`/api/v1/checklists/${checklistId}/related-incidents`);
  }
  async getChecklistSchedules(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/schedules${buildQueryString(params)}`);
  }
  async getChecklistTemplate(id: number): Promise<any> {
    return this.get(`/api/v1/checklists/templates/${id}`);
  }
  async getChecklistTemplateDispatch(templateId: number): Promise<any> {
    return this.get(`/api/v1/checklists/templates/${templateId}/dispatch`);
  }
  async getChecklistTemplatePresets(category?: string): Promise<any[]> {
    return this.get(`/api/v1/checklists/templates/presets${buildQueryString({ category })}`);
  }
  async getChecklistTemplateQr(templateId: number): Promise<{
    template_id: number;
    token: string;
    version: number;
    deep_link: string;
  }> {
    return this.get(`/api/v1/checklists/templates/${templateId}/qr`);
  }
  async getChecklistTemplates(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/checklists/templates${buildQueryString(params)}`);
  }
  async getChecklists(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/checklists${buildQueryString(params)}`);
  }
  async getChecklistsMissedInstances(
    params: { date_from?: string; date_to?: string; location_id?: number; category?: string } = {},
  ): Promise<any[]> {
    return this.get(`/api/v1/checklists/missed-instances${buildQueryString(params)}`);
  }
  async getChecklistsProblemItems(params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/analytics/problem-items${buildQueryString(params || {})}`);
  }
  // ─── Checklist Related Incidents ─────────────────────────────────────────────
  async getContractorTemplate(id: number): Promise<any> {
    return this.get(`/api/v1/checklists/contractors/templates/${id}`);
  }
  async getContractorTemplateQr(templateId: number): Promise<any> {
    return this.get(`/api/v1/checklists/contractors/templates/${templateId}/qr`);
  }
  async getContractorTemplates(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/checklists/contractors/templates${buildQueryString(params)}`);
  }
  async getContractorVisit(checklistId: number): Promise<any> {
    return this.get(`/api/v1/checklists/contractors/visits/${checklistId}`);
  }
  async getContractorVisits(params: Record<string, unknown> = {}): Promise<any[]> {
    return this.get(`/api/v1/checklists/contractors/visits${buildQueryString(params)}`);
  }
  async regenerateChecklistTemplateQr(templateId: number): Promise<{
    token: string;
    version: number;
    deep_link: string;
  }> {
    return this.post(`/api/v1/checklists/templates/${templateId}/qr/regenerate`);
  }
  async regenerateContractorTemplateQr(templateId: number): Promise<any> {
    return this.post(`/api/v1/checklists/contractors/templates/${templateId}/qr/regenerate`);
  }
  async reopenCounterpartyChecklist(checklistId: number): Promise<any> {
    return this.post(`/api/v1/counterparties/checklists/${checklistId}/reopen`);
  }
  async reopenWalletChecklist(checklistId: number): Promise<any> {
    return this.post(`/api/v1/wallet/checklists/${checklistId}/reopen`);
  }
  // ─── Counterparties (hospitality) ───────────────────────────────────────────
  async reorderChecklistTemplateItems(templateId: number, items: { id: number; order_index: number }[]): Promise<any> {
    return this.put(`/api/v1/checklists/templates/${templateId}/items/reorder`, { items });
  }
  async rescheduleChecklist(
    id: number,
    body: { new_date: string; new_shift_id?: number | null; allow_without_shift?: boolean },
  ): Promise<any> {
    return this.patch(`/api/v1/checklists/${id}/reschedule`, body);
  }
  async retryChecklistDelivery(checklistId: number): Promise<{ ok: boolean; reset: number; message?: string }> {
    return this.post(`/api/v1/checklists/${checklistId}/delivery/retry`, {});
  }
  async saveChecklistDraft(id: number, answers: Record<string, unknown>[]): Promise<any> {
    return this.patch(`/api/v1/checklists/${id}/draft`, { answers });
  }
  async startChecklist(data: any): Promise<any> {
    return this.post('/api/v1/checklists/start', data);
  }
  async submitChecklist(id: number, answers: Record<string, unknown>[]): Promise<any> {
    return this.post(`/api/v1/checklists/${id}/submit`, { answers });
  }
  async testChecklistTemplateEmail(
    templateId: number,
    email: string,
    payload?: { email_subject?: string; email_body?: string }
  ): Promise<any> {
    return this.post(
      `/api/v1/checklists/templates/${templateId}/dispatch/test-email?to_address=${encodeURIComponent(email)}`,
      payload ?? {}
    );
  }
  async testChecklistTemplateTelegram(templateId: number, chatId: string): Promise<any> {
    return this.post(
      `/api/v1/checklists/templates/${templateId}/dispatch/test-telegram?chat_id=${encodeURIComponent(chatId)}`
    );
  }
  async updateChecklistBlock(templateId: number, blockId: number, data: any): Promise<any> {
    return this.put(`/api/v1/checklists/templates/${templateId}/blocks/${blockId}`, data);
  }
  async updateChecklistPdfBranding(data: any): Promise<any> {
    return this.put('/api/v1/checklists/pdf-branding', data);
  }
  async updateChecklistTemplate(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/checklists/templates/${id}`, data);
  }
  async updateChecklistTemplateDispatch(templateId: number, data: any): Promise<any> {
    return this.put(`/api/v1/checklists/templates/${templateId}/dispatch`, data);
  }
  async updateChecklistTemplateItem(templateId: number, itemId: number, data: any): Promise<any> {
    return this.put(`/api/v1/checklists/templates/${templateId}/items/${itemId}`, data);
  }
  async updateContractorTemplate(
    id: number,
    data: {
      name?: string;
      description?: string;
      contractor_role_label?: string;
      location_ids?: number[];
      is_active?: boolean;
      qr_activation_enabled?: boolean;
    }
  ): Promise<any> {
    return this.put(`/api/v1/checklists/contractors/templates/${id}`, data);
  }
  async getPersonalChecklistsByEmployee(params: {
    employee_id: number;
    date_from: string;
    date_to: string;
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/personal-by-employee${buildQueryString(params)}`);
  }
}
